import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandBadge, { Wordmark } from "./BrandBadge";
import LockUnlock from "./LockUnlock";
import { useAuth } from "./AuthContext";
import { useStoreMeta } from "./StoreMetaContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { LayoutDashboard, Upload, List, BarChart3, LogOut, AlertTriangle, Trophy, Store, ShieldCheck, Swords, Menu, X, HelpCircle, Brain, Flame, Database, Gamepad2, TrendingUp, Gauge, Rocket, Users, Sparkles, CalendarClock } from "lucide-react";
import TutorialPopup from "./TutorialPopup";
import TwoFactorDialog from "./TwoFactorDialog";
import { TraineeApprovalDialog } from "./TraineeApprovalDialog";
import GrillIcon from "./icons/GrillIcon";
import StoreGraphIcon from "./icons/StoreGraphIcon";
import DomiVisionIcon from "./icons/DomiVisionIcon";

// Domi-Grill launched 26th May 2026 — "NEW" badge auto-retires after 30 days
const DOMIGRILL_LAUNCH_DATE = new Date("2026-05-26");
const DOMIGRILL_NEW_BADGE_DAYS = 30;
const isDomiGrillNew = () => {
  const elapsedDays = (Date.now() - DOMIGRILL_LAUNCH_DATE.getTime()) / 86400000;
  return elapsedDays >= 0 && elapsedDays < DOMIGRILL_NEW_BADGE_DAYS;
};

// Welcome tutorial is shown only ONCE per device (per role). The flag lives in
// localStorage so it survives logout/login. "Show tour again" in Help re-opens
// it on demand via the `open-tutorial` window event without clearing the flag.
const TUTORIAL_KEYS = { admin: "tutorial_seen_admin_v2", store: "tutorial_seen_store_v2" };
const tutorialSeen = (role) => {
  try { return !!localStorage.getItem(TUTORIAL_KEYS[role]); } catch { return false; }
};
const markTutorialSeen = (role) => {
  try { if (TUTORIAL_KEYS[role]) localStorage.setItem(TUTORIAL_KEYS[role], "1"); } catch { /* ignore */ }
};

export default function Navbar() {
  const location = useLocation();
  const { isAdmin, isSuperAdmin, isAreaManager, storeRole, adminLevel, isDemoAccount, loginUnified, enterTwoFactor, logout, verifyStoreDevice } = useAuth();
  const { isDemo: metaIsDemo } = useStoreMeta();
  const isDemo = isDemoAccount || metaIsDemo;
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  // Trusted-device verification (store logins on a new device)
  const [deviceVerify, setDeviceVerify] = useState(null); // { pin, store } | null
  const [deviceCode, setDeviceCode] = useState("");
  const [deviceLabel, setDeviceLabel] = useState("");
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [deviceAccepted, setDeviceAccepted] = useState(false);
  const [traineePreAuth, setTraineePreAuth] = useState(null);

  const handleVerifyDevice = async () => {
    if (deviceCode.trim().length !== 6) { toast.error("Enter the 6-digit manager code"); return; }
    setDeviceBusy(true);
    const r = await verifyStoreDevice(deviceVerify.pin, deviceCode.trim(), deviceLabel.trim() || "Store device");
    setDeviceBusy(false);
    if (r.valid) {
      setDeviceAccepted(true);
      setTimeout(() => {
        toast.success(`Store ${r.store_code} unlocked · this device is now trusted`);
        setDeviceVerify(null); setShowLoginDialog(false); setDeviceCode(""); setDeviceLabel(""); setDeviceAccepted(false);
      }, 1050);
      if (!tutorialSeen("store")) { setTutorialRole("store"); setShowTutorial(true); }
    } else {
      toast.error(r.error || "Invalid manager approval code");
    }
  };
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [adminUnlocking, setAdminUnlocking] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // Shown if the verify call is taking > 1.5s so the user knows the spinner
  // isn't broken — usually means the server pod is cold-starting after idle.
  const [slowHint, setSlowHint] = useState(false);
  const [lockUntil, setLockUntil] = useState(0);
  const [lockType, setLockType] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [demoMode, setDemoMode] = useState(false);

  const enterDemoMode = () => {
    setDemoMode(true);
    setUsername("demoAM");
    setPassword("");
    setLockUntil(0);
  };
  const exitDemoMode = () => {
    setDemoMode(false);
    setUsername("");
    setPassword("");
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialRole, setTutorialRole] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [prevAdminLevel, setPrevAdminLevel] = useState(adminLevel);

  // When 2FA completes, AuthContext flips adminLevel from null → 'super'/'area'.
  // Fire the tutorial popup at that transition — but only the first time on
  // this device (per the localStorage flag).
  if (adminLevel !== prevAdminLevel) {
    if (!prevAdminLevel && adminLevel && !tutorialSeen("admin") && !isDemo) {
      setTutorialRole("admin");
      setShowTutorial(true);
    }
    setPrevAdminLevel(adminLevel);
  }

  // "Show tour again" (from Help) dispatches this event to re-open the tour
  // for the current role on demand, regardless of the seen flag.
  useEffect(() => {
    const handler = (e) => {
      if (isDemo) return;   // demo account gets no tutorial
      setTutorialRole(isAdmin ? "admin" : "store");
      setTutorialStep(e?.detail?.step || 0);
      setShowTutorial(true);
    };
    window.addEventListener("open-tutorial", handler);
    return () => window.removeEventListener("open-tutorial", handler);
  }, [isAdmin, isDemo]);

  const isLoggedIn = isAdmin || !!storeRole;

  // Landing page CTA dispatches this to open the login dialog
  useEffect(() => {
    const handler = () => { setShowLoginDialog(true); setUsername(""); setPassword(""); };
    window.addEventListener("open-login", handler);
    return () => window.removeEventListener("open-login", handler);
  }, []);

  const navItemsAll = isSuperAdmin
    ? [
        { to: "/coach", label: "DOMI-COACH", icon: Brain },
        { to: "/dashboard", label: "DATA INPUT", icon: LayoutDashboard },
        { to: "/submissions", label: "SUBMISSIONS", icon: List },
        { to: "/kpi", label: "Performance", icon: BarChart3 },
        { to: "/complaints", label: "FEEDBACK", icon: AlertTriangle },
        { to: "/weekly-tracker", label: "KPI TRACKER", icon: Trophy },
        { to: "/sales-building", label: "SALES BUILDING", icon: StoreGraphIcon },
        { to: "/beyond-operations", label: "BEYOND OPERATIONS", icon: Rocket },
        { to: "/dating-tool", label: "DATING TOOL", icon: CalendarClock },
        { to: "/help", label: "HELP", icon: HelpCircle },
      ]
    : isAreaManager
    ? [
        // Area Manager nav: read all stores, Domi-Vision admin paste, no Data Input /
        // Submissions / Password change. Domi-Heist and Domi-Grill are intentionally
        // excluded — gamified store-vs-store competition is for Super Admin + Stores only.
        { to: "/coach", label: "DOMI-COACH", icon: Brain },
        { to: "/kpi", label: "Performance", icon: BarChart3 },
        { to: "/complaints", label: "FEEDBACK", icon: AlertTriangle },
        { to: "/weekly-tracker", label: "KPI TRACKER", icon: Trophy },
        { to: "/sales-building", label: "SALES BUILDING", icon: StoreGraphIcon },
        { to: "/beyond-operations", label: "BEYOND OPERATIONS", icon: Rocket },
        { to: "/dating-tool", label: "DATING TOOL", icon: CalendarClock },
        { to: "/help", label: "HELP", icon: HelpCircle },
      ]
    : storeRole
    ? [
        // Store nav — order matches user request: Submit → Domi-Coach → Performance →
        // Feedback → KPI Tracker → Sales Building → Domi-Vision → Help → (Log out via badge).
        { to: "/submit", label: "NEW SUBMISSION", icon: Upload },
        { to: "/coach", label: "DOMI-COACH", icon: Brain },
        { to: "/kpi", label: "PERFORMANCE", icon: BarChart3 },
        { to: "/complaints", label: "FEEDBACK", icon: AlertTriangle },
        { to: "/weekly-tracker", label: "KPI TRACKER", icon: Trophy },
        { to: "/sales-building", label: "SALES BUILDING", icon: StoreGraphIcon },
        { to: "/beyond-operations", label: "BEYOND OPERATIONS", icon: Rocket },
        { to: "/dating-tool", label: "DATING TOOL", icon: CalendarClock },
        { to: "/help", label: "HELP", icon: HelpCircle },
      ]
    : [];

  // Demo account: Beyond Operations (Domi-Vision / Domi-Grill / Domi-Heist) is
  // now OPEN as a showcase, so every nav item is available.
  const navItems = navItemsAll;

  // Tick the lockout countdown down each second; auto-clears at zero.
  useEffect(() => {
    if (!lockUntil) return undefined;
    const id = setInterval(() => {
      if (Date.now() >= lockUntil) setLockUntil(0);
      setNowTs(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [lockUntil]);

  const lockSeconds = lockUntil ? Math.max(0, Math.ceil((lockUntil - nowTs) / 1000)) : 0;
  const fmtLock = (s) => (s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : `${s}s`);

  // Unified login: one username + password. The server decides the account
  // type. Store/trainee complete here; admin returns pre-auth data and the
  // TwoFactorDialog (mounted below) drives the 6-digit code step.
  const handleLogin = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setSlowHint(false);
    const slowTimer = setTimeout(() => setSlowHint(true), 1500);
    const result = await loginUnified(username.trim(), password);
    clearTimeout(slowTimer);
    setVerifying(false);
    setSlowHint(false);

    if (result.kind === "store") {
      if (result.valid) {
        setLockUntil(0);
        toast.success(`Store ${result.store_code} unlocked`);
        setShowLoginDialog(false);
        setUsername(""); setPassword("");
        if (!tutorialSeen("store")) { setTutorialRole("store"); setShowTutorial(true); }
      } else if (result.requiresDeviceVerification) {
        setShowLoginDialog(false);
        setUsername(""); setPassword("");
        setDeviceVerify({ pin: result.pin, store: result.store_code });
        setDeviceCode(""); setDeviceLabel("");
      }
      return;
    }

    if (result.kind === "trainee") {
      setShowLoginDialog(false);
      setUsername(""); setPassword("");
      setTraineePreAuth(result.preAuthToken);
      return;
    }

    if (!result.valid) {
      if (result.locked) {
        setLockType(result.lockType); setNowTs(Date.now()); setLockUntil(Date.now() + (result.retryAfter || 0) * 1000);
      } else {
        toast.error(result.error || "Invalid username or password");
      }
      return;
    }

    // Admin: play unlock → Token ID (2FA) screen.
    setLockUntil(0);
    setAdminUnlocking(true);
    setTimeout(() => {
      setAdminUnlocking(false);
      setShowLoginDialog(false);
      setUsername("");
      setPassword("");
      enterTwoFactor(result);
      if (!result.enrolled) {
        toast.info(`${result.tier === "super" ? "Super Admin" : "Area Manager"} — set up your authenticator`);
      }
    }, 1050);
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    setMobileOpen(false);
  };

  return (
    <>
      {/* ═══ Desktop left sidebar (lg+ only) — collapsed by default, hover-expands ═══ */}
      <DesktopSidebar
        navItems={navItems}
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        storeRole={storeRole}
        onLogout={handleLogout}
      />

      {/* ═══ Mobile top bar (<lg) — original layout kept for phones/tablets ═══ */}
      <nav data-testid="main-navbar" className="lg:hidden bg-white/80 backdrop-blur-xl text-[#1d1d1f] border-b border-black/[0.08] relative z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/coach" className="flex items-center group" data-testid="nav-logo">
              <Wordmark className="text-2xl" testId="nav-wordmark" />
            </Link>

            {/* Mobile: role badge + hamburger */}
            <div className="flex items-center gap-2">
              {isLoggedIn && (
                <span className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-black/[0.06] rounded-sm ${isAdmin ? 'text-green-300' : 'text-[#bf6800]'}`} style={{ fontFamily: 'Inter, sans-serif' }}>
                  {isAdmin ? <ShieldCheck size={10} /> : <Store size={10} />}
                  {isAdmin ? "ADMIN" : storeRole?.code}
                </span>
              )}
              {isLoggedIn && (
                <button
                  data-testid="mobile-menu-btn"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="p-2 text-[#1d1d1f]/70 hover:text-[#1d1d1f] hover:bg-black/[0.05] rounded-sm transition-colors"
                >
                  {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu panel — clean vertical list view (no grid tiles) */}
        {mobileOpen && isLoggedIn && (
          <div className="lg:hidden border-t border-black/[0.08] bg-white/95 backdrop-blur-xl" data-testid="mobile-menu-panel">
            <div className="px-2 py-2">
              <div className="divide-y divide-white/10" data-testid="mobile-nav-list">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.to;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-3 transition-colors ${
                        isActive ? "bg-black/[0.06] text-[#bf6800]" : "text-[#1d1d1f]/70 hover:bg-black/[0.05]"
                      }`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {isActive && <span className="absolute left-0 w-1 h-7 bg-[#bf6800] rounded-r" />}
                      <Icon size={20} strokeWidth={1.7} className="flex-shrink-0" />
                      <span className="text-sm font-bold uppercase tracking-wider">{item.label}</span>
                    </Link>
                  );
                })}
                <button
                  onClick={handleLogout}
                  data-testid="mobile-logout-btn"
                  className="w-full flex items-center gap-3 px-3 py-3 text-rose-300 hover:bg-rose-500/15 transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <LogOut size={20} strokeWidth={1.7} className="flex-shrink-0" />
                  <span className="text-sm font-bold uppercase tracking-wider">Log Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <TraineeApprovalDialog preAuthToken={traineePreAuth} onClose={() => setTraineePreAuth(null)} />

      {/* Trusted-device verification (store login on a new/unrecognised device) */}
      <Dialog open={!!deviceVerify} onOpenChange={(o) => { if (!o) setDeviceVerify(null); }}>
        <DialogContent className="max-w-sm" data-testid="device-verify-dialog">
          <DialogHeader className="items-center text-center">
            <LockUnlock size={64} mode={deviceAccepted ? "once" : "locked"} data-testid="device-verify-lock" />
            <DialogTitle className="text-xl font-semibold uppercase tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              {deviceAccepted ? "Device trusted" : "Verify this device"}
            </DialogTitle>
            <DialogDescription>
              {deviceAccepted
                ? `Signing you in to store ${deviceVerify?.store}…`
                : <>This device isn't recognised for store <b>{deviceVerify?.store}</b>. Ask an Area or Regional Manager to enter their 6-digit authenticator code once. This device will then be remembered.</>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleVerifyDevice(); }} className="space-y-3">
            <input
              data-testid="device-verify-code"
              autoFocus inputMode="numeric" maxLength={6} placeholder="6-digit manager code"
              value={deviceCode} onChange={(e) => setDeviceCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full text-center tracking-[0.4em] text-lg font-bold border border-black/[0.15] rounded-lg py-2.5"
            />
            <input
              data-testid="device-verify-label" placeholder="Device name (e.g. Front counter iPad)"
              value={deviceLabel} onChange={(e) => setDeviceLabel(e.target.value)}
              className="w-full text-sm border border-black/[0.15] rounded-lg px-3 py-2"
            />
            <button type="submit" disabled={deviceBusy || deviceCode.length !== 6} data-testid="device-verify-submit"
              className="w-full py-2.5 rounded-full font-black text-white bg-[#0071e3] disabled:opacity-40">
              {deviceBusy ? "Verifying…" : "Trust this device"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unified Log-In Dialog — accepts a Store PIN, Area Manager password,
          or Super Admin password. After the password is verified, admin tiers
          are sent through TwoFactorDialog (TOTP) before access is granted. */}
      <Dialog
        open={showLoginDialog}
        onOpenChange={(open) => {
          setShowLoginDialog(open);
          // Fire a warm-up ping the moment the dialog opens so the
          // server pod is already hot by the time the user finishes
          // typing their code. Best-effort — silent on failure.
          if (open) {
            import("axios").then(({ default: axios }) => {
              axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/health/ping`).catch(() => {});
            });
          } else {
            setDemoMode(false);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <Wordmark className="text-4xl" testId="login-wordmark" />
            </div>
            <DialogTitle className="text-xl font-semibold uppercase tracking-tight flex items-center justify-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              <ShieldCheck size={18} className="text-[#0071e3]" />
              {demoMode ? "Explore the Demo" : "Log in"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Enter your username and password. Back-office accounts will also be prompted for a 6-digit code from their authenticator app.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4 mt-2">
            {adminUnlocking ? (
              <div className="flex flex-col items-center gap-3 py-6" data-testid="login-unlocking">
                <LockUnlock size={72} mode="once" />
                <p className="text-sm text-[#6e6e73]">Password accepted…</p>
              </div>
            ) : (
              <>
                {lockSeconds > 0 && (
                  <div data-testid="login-lockout-banner" className="rounded-xl border border-[#e7dcc4] bg-[#fdf9f0] px-4 py-3 text-center">
                    <p className="text-sm font-medium text-[#1d1d1f]">
                      {lockType === "soft" ? "Just a moment" : "Too many attempts"}
                    </p>
                    <p className="text-xs text-[#86868b] mt-0.5">
                      You can try again in{" "}
                      <span data-testid="login-lockout-countdown" className="font-semibold text-[#bf6800] tabular-nums">{fmtLock(lockSeconds)}</span>
                    </p>
                  </div>
                )}
                {demoMode && (
                  <div data-testid="demo-mode-banner" className="rounded-xl border border-[#cfe3ff] bg-[#f0f7ff] px-4 py-3 text-center">
                    <p className="text-sm font-semibold text-[#0071e3]">Demo workspace — Area Manager</p>
                    <p className="text-[11px] text-[#6e6e73] mt-0.5">5 fictional stores · fully separate from live data. Real password &amp; 2FA still required.</p>
                  </div>
                )}

                <Input
                  data-testid="login-username-input"
                  type="text"
                  name="username"
                  autoComplete={rememberMe ? "username" : "off"}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  readOnly={demoMode}
                  className={`border-black/[0.12] rounded-lg ${demoMode ? "bg-[#f5f5f7] text-[#6e6e73]" : ""}`}
                  autoFocus={!demoMode}
                />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  name="password"
                  autoComplete={rememberMe ? "current-password" : "off"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="border-black/[0.12] rounded-lg"
                  autoFocus={demoMode}
                />

                <label className="flex items-center gap-2 text-sm text-[#1d1d1f] select-none cursor-pointer" data-testid="login-remember-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    data-testid="login-remember-checkbox"
                    className="h-4 w-4 rounded border-black/30 accent-[#0071e3]"
                  />
                  Remember me on this device
                </label>

                <Button
                  type="submit"
                  disabled={verifying || lockSeconds > 0 || !username.trim() || !password}
                  data-testid="login-dialog-submit"
                  className="w-full bg-[#0071e3] hover:bg-[#0060c0] text-white rounded-full font-medium"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {verifying ? "Verifying..." : lockSeconds > 0 ? `Try again in ${fmtLock(lockSeconds)}` : "Log in"}
                </Button>
                {slowHint && (
                  <p data-testid="login-slow-hint" className="text-[11px] text-center text-[#86868b] -mt-1 leading-snug">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#bf6800] animate-pulse mr-1 align-middle" />
                    Waking the server up… first login of the day can take a few seconds.
                  </p>
                )}

                {demoMode ? (
                  <button
                    type="button" data-testid="exit-demo-btn" onClick={exitDemoMode}
                    className="w-full text-xs text-[#6e6e73] hover:text-[#1d1d1f] transition-colors"
                  >← Use a normal login instead</button>
                ) : (
                  <button
                    type="button" data-testid="explore-demo-btn" onClick={enterDemoMode}
                    className="w-full text-xs text-[#6e6e73] hover:text-[#0071e3] transition-colors inline-flex items-center justify-center gap-1"
                  >
                    <Sparkles size={12} strokeWidth={2} /> Explore the demo
                  </button>
                )}
              </>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Two-factor authentication dialog (admin tiers only). Renders when
          AuthContext.pending2FA is set — i.e. after the password is verified
          but before the session is granted. */}
      <TwoFactorDialog />

      {/* Tutorial Popup */}
      {showTutorial && (
        <TutorialPopup
          initialStep={tutorialStep}
          isAdmin={tutorialRole === "admin"}
          isStore={tutorialRole === "store"}
          onClose={() => { markTutorialSeen(tutorialRole); setShowTutorial(false); }}
        />
      )}
    </>
  );
}


/**
 * Fixed left sidebar — desktop only (lg+). Collapsed at 64px showing icons only,
 * hover-expands to 240px revealing labels. Domino's logo at the top links to
 * the Coach (Domi-Coach).
 *
 * The sidebar is positioned `fixed left-0 top-0 h-screen z-40`. App.js shifts
 * the main content right with `lg:pl-16` so nothing sits underneath.
 */
function DesktopSidebar({ navItems, isLoggedIn, isAdmin, storeRole, onLogout }) {
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  const expanded = hovered;
  const W = expanded ? 240 : 64;

  return (
    <aside
      data-testid="desktop-sidebar"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: W }}
      className="hidden lg:flex fixed left-0 top-0 h-screen z-40 flex-col bg-white/80 backdrop-blur-xl text-[#1d1d1f] border-r border-black/[0.08] transition-[width] duration-200 ease-out overflow-hidden"
    >
      {/* Logo header — clicking lands on Coach */}
      <Link
        to="/coach"
        data-testid="sidebar-logo"
        className="flex items-center gap-3 h-14 px-3 border-b border-black/[0.08] hover:bg-black/[0.04] transition-colors flex-shrink-0"
      >
        {expanded ? (
          <Wordmark className="text-2xl whitespace-nowrap" testId="sidebar-wordmark" />
        ) : (
          <BrandBadge size={26} className="flex-shrink-0" />
        )}
      </Link>

      {/* Nav items */}
      <div className="flex-1 overflow-y-auto py-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to;
          const Icon = item.icon;
          const isWar = item.label === "DOMI-HEIST";
          const isGrill = item.label === "DOMI-GRILL";

          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              title={item.label}
              className={`relative flex items-center gap-3 h-11 px-3 transition-colors ${
                isWar
                  ? "bg-black/30 hover:bg-black/50"
                  : isGrill
                  ? "bg-[#bf6800]/10 hover:bg-[#bf6800]/20"
                  : isActive
                  ? "bg-black/[0.06] text-[#bf6800]"
                  : "text-[#1d1d1f]/70 hover:text-[#1d1d1f] hover:bg-black/[0.05]"
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {/* Active indicator pill on the left */}
              {isActive && (
                <span className="absolute left-0 top-2 bottom-2 w-1 bg-[#bf6800] rounded-r" />
              )}
              <Icon size={20} strokeWidth={1.7} className="flex-shrink-0" />
              <span
                className={`text-[13px] font-bold tracking-wider uppercase whitespace-nowrap transition-opacity duration-200 ${expanded ? "opacity-100" : "opacity-0"}`}
              >
                {item.label}
              </span>
              {isGrill && expanded && isDomiGrillNew() && (
                <span className="ml-auto text-[9px] font-black bg-[#bf6800] text-white px-1 py-0.5 rounded">NEW</span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Footer — role badge + logout (anonymous visitors log in from the landing page) */}
      <div className={`border-t border-black/[0.08] px-2 py-3 flex-shrink-0 ${isLoggedIn ? "" : "hidden"}`}>
        {isLoggedIn ? (
          <>
            <div className={`flex items-center gap-2 px-2 py-1.5 mb-1 rounded ${isAdmin ? "bg-green-500/10 text-green-300" : "bg-[#bf6800]/10 text-[#bf6800]"}`}>
              {isAdmin ? <ShieldCheck size={14} strokeWidth={1.8} className="flex-shrink-0" /> : <Store size={14} strokeWidth={1.8} className="flex-shrink-0" />}
              <span className={`text-xs font-black tracking-wider whitespace-nowrap transition-opacity duration-200 ${expanded ? "opacity-100" : "opacity-0"}`} style={{ fontFamily: 'Inter, sans-serif' }}>
                {isAdmin ? "ADMIN" : storeRole?.code}
              </span>
            </div>
            <button
              onClick={onLogout}
              data-testid="sidebar-logout-btn"
              title="Log out"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[#1d1d1f]/55 hover:text-[#1d1d1f] hover:bg-black/[0.05] transition-colors"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <LogOut size={14} strokeWidth={1.8} className="flex-shrink-0" />
              <span className={`text-xs font-bold tracking-wider uppercase whitespace-nowrap transition-opacity duration-200 ${expanded ? "opacity-100" : "opacity-0"}`}>
                LOG OUT
              </span>
            </button>
          </>
        ) : null}
      </div>
    </aside>
  );
}
